import { FontDisplay, loadAsync, useFonts } from 'expo-font';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

// Patch FontFaceObserver on web to prevent unhandled '12000ms timeout exceeded' rejections.
// FontFaceObserver tests glyph dimensions against a fallback font with a 12s timeout.
// On throttled background tabs, slow networks, or icon fonts (which lack standard Latin test glyphs),
// it rejects after 12000ms even though the @font-face stylesheet is already injected into document.head.
if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
        const FontFaceObserver = require('fontfaceobserver');
        if (FontFaceObserver && FontFaceObserver.prototype && !FontFaceObserver.prototype.__ss_patched) {
            FontFaceObserver.prototype.__ss_patched = true;
            const originalLoad = FontFaceObserver.prototype.load;
            FontFaceObserver.prototype.load = function (testString?: string | null, timeout?: number) {
                return originalLoad.call(this, testString, timeout).catch((err: any) => {
                    if (typeof console !== 'undefined' && console.warn) {
                        console.warn(`[useAppFonts] FontFaceObserver timed out for "${this.family}". Continuing with fallback font.`);
                    }
                    return this;
                });
            };
        }

        window.addEventListener('unhandledrejection', (event) => {
            if (event?.reason?.message && event.reason.message.includes('timeout exceeded')) {
                event.preventDefault();
                if (typeof console !== 'undefined' && console.warn) {
                    console.warn('[useAppFonts] Suppressed font timeout rejection:', event.reason.message);
                }
            }
        });
    } catch {}
}

// `display: swap` only affects the web @font-face output (see
// ExpoFontLoader.web.ts) — native ignores it and already emulates swap
// behaviour by default. Without it, web defaults to `auto`, which on most
// browsers means invisible text until the woff2 loads (FOIT).
const FONTS = {
    ...Ionicons.font,
    'Metropolis-Regular': { uri: require('../../assets/fonts/metropolis-400.woff2'), display: FontDisplay.SWAP },
    'Metropolis-Medium': { uri: require('../../assets/fonts/metropolis-500.woff2'), display: FontDisplay.SWAP },
    'Metropolis-SemiBold': { uri: require('../../assets/fonts/metropolis-600.woff2'), display: FontDisplay.SWAP },
    'Metropolis-Bold': { uri: require('../../assets/fonts/metropolis-700.woff2'), display: FontDisplay.SWAP }
};

function useAppFonts(): [boolean, Error | null] {
    if (Platform.OS !== 'web') {
        return useFonts(FONTS);
    }

    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let isMounted = true;

        // On web, ExpoFontLoader injects the @font-face rules synchronously into document.head.
        // However, FontFaceObserver's 12000ms verification timer rejects asynchronously
        // on throttled background tabs or subtle metric mismatches. We catch and swallow
        // any observer timeout so the app continues seamlessly with swap / fallbacks.
        loadAsync(FONTS)
            .catch(() => {
                // Swallowed: @font-face stylesheet is already injected into document.head
            })
            .finally(() => {
                if (isMounted) {
                    setLoaded(true);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    return [loaded, null];
}

export { useAppFonts };
